/**
 * The Team page's wire (ADR-0044, PR 2 Task 7).
 *
 * The whole configuration is one page per Team, so the whole client is one
 * module hung off `/api/desks/:id`. There are deliberately no per-Engagement
 * calls: an Engagement is a grouping on the page, not a screen.
 *
 * Two shapes here are mirrors rather than imports, and it is worth saying why:
 * `JobRecord` and `DeskMember` live in the backend and never made it into
 * `@yagni/shared` in PR 1. Mirroring them is the smaller lie than reaching
 * across packages, and both are read-only shapes the routes already fix.
 */

import type {
  ConnectionTeamsResponse,
  DeskAttachment,
  HarperApproach,
  HarperPreview,
  Job,
  JobAuthority,
  JobBody,
  JobStatus,
  RepoSuggestion,
  TeamJobCatalogResponse,
  TeamJobRunsResponse,
  TeamReadsResponse,
  TeamTicketOwners,
  TrackerOptionsResponse,
  TrackerSearchResponse,
  WrittenAs,
} from "@yagni/shared";
import { parseWorkspaceRepos, type WorkspaceRepo } from "../lib/workspaceRepos.js";

const deskBase = (deskId: string): string => `/api/desks/${encodeURIComponent(deskId)}`;

/** One Agent's lines on this Team, as `GET /:id/jobs` groups them. */
export interface TeamJobGroup {
  personaSlug: string;
  displayName: string;
  craft: string | null;
  jobs: Job[];
}

/** The Team's brake, with the name the header prints. */
export interface TeamPaused {
  paused: boolean;
  pausedAt: string | null;
  pausedByUserId: string | null;
  pausedByName: string | null;
}

/** A person on the Team. Mirrors `desks/members.ts`. */
export interface TeamMember {
  userId: string;
  role: "owner" | "member";
  addedAt: string;
  displayName: string | null;
  email: string | null;
  /** Their profile photo (Google), or null for initials. Absent on older servers. */
  avatarUrl?: string | null;
  /** Their @mention handle, so a comment can address them. Absent on older servers. */
  handle?: string | null;
}

/** The three kinds a Team names answerers for, keyed by kind. */
export type TeamAnswerers = Record<string, string[]>;

/** A tracker the workspace has connected, as the members read names it. */
export type TeamTracker = "jira" | "linear";

/** `GET /:id/members`: the roster, and the Team settings whose entries are people. */
export interface TeamMembersResponse {
  members: TeamMember[];
  answerers: TeamAnswerers;
  /**
   * Whose tracker tickets this Team works from: member user ids and
   * `"unassigned"`. Null means anyone's.
   */
  ticketsAssignedTo: TeamTicketOwners;
  /** The workspace's connected trackers, so the page can say "my Jira tickets". */
  trackers: TeamTracker[];
}

/**
 * One line's record, as counts. Mirrors `workers/jobRecord.ts`.
 *
 * Counts, never a suggestion: nothing in here advises a rung and nothing in
 * here can move one. `total` is what lets the surface decide a rare line has
 * too little volume for a record line at all (spec edge case 13).
 */
export interface TeamJobRecord {
  jobId: string;
  /** A person took the act exactly as the line proposed it. */
  acceptedAsProposed: number;
  /** A person took it, with changes. Forward-positive, like an acceptance. */
  edited: number;
  sentBack: number;
  /** The line dispatched it alone, under Autonomous. */
  autoShipped: number;
  /** Somebody reversed an act of this line. One more count, never a demotion. */
  reversed: number;
  total: number;
  promoted: Array<{ at: string; actorUserId: string | null }>;
  demoted: Array<{ at: string; actorUserId: string | null }>;
}

export interface TeamJobsResponse {
  engagements: TeamJobGroup[];
  paused: TeamPaused;
}

/** A refusal a person caused, with the code the copy keys off. */
export class TeamWriteError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(
    message: string,
    code: string,
    status: number,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "TeamWriteError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: string;
      code?: string;
      details?: Record<string, unknown>;
    } | null;
    throw new TeamWriteError(
      body?.error ?? `Request failed (${res.status}).`,
      body?.code ?? "request_failed",
      res.status,
      body?.details ?? {},
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function get<T>(path: string): Promise<T> {
  return fetch(path, { credentials: "include" }).then(readJson<T>);
}

function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  return fetch(path, {
    method,
    credentials: "include",
    ...(body === undefined
      ? {}
      : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
  }).then(readJson<T>);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function fetchTeamJobs(deskId: string): Promise<TeamJobsResponse> {
  return get<TeamJobsResponse>(`${deskBase(deskId)}/jobs`);
}

/** `GET /api/desks/:id/attachments`: the Team's repositories and backlogs. */
export function fetchTeamAttachments(deskId: string): Promise<DeskAttachment[]> {
  return get<DeskAttachment[] | { attachments?: DeskAttachment[] }>(
    `${deskBase(deskId)}/attachments`,
  ).then((body) => (Array.isArray(body) ? body : (body.attachments ?? [])));
}

export function fetchTeamJobStatus(deskId: string): Promise<JobStatus[]> {
  return get<{ jobs: JobStatus[] }>(`${deskBase(deskId)}/jobs/status`).then((body) => body.jobs);
}

export function fetchTeamCatalog(deskId: string): Promise<TeamJobCatalogResponse> {
  return get<TeamJobCatalogResponse>(`${deskBase(deskId)}/jobs/catalog`);
}

/** A repository a Team's tickets can build in: attached AND in the workspace. */
export type TeamRepo = WorkspaceRepo;

/**
 * The Team's buildable repositories, as the server's `listTeamRepos` reads
 * them: its repo attachments that the workspace has connected, by name,
 * sorted. Two reads joined here because no single route answers it.
 */
export async function fetchTeamRepos(deskId: string): Promise<TeamRepo[]> {
  const [attached, workspace] = await Promise.all([
    get<{ attachments?: Array<{ kind?: unknown; ref?: unknown }> }>(
      `${deskBase(deskId)}/attachments`,
    ),
    get<unknown>("/api/repos"),
  ]);
  const refs = new Set(
    (attached.attachments ?? [])
      .filter((entry) => entry.kind === "repo" && typeof entry.ref === "string")
      .map((entry) => String(entry.ref).toLowerCase()),
  );
  return parseWorkspaceRepos(workspace).filter((repo) => refs.has(repo.fullName.toLowerCase()));
}

/** What a ticket says about itself, for `fetchRepoSuggestion`. */
export interface RepoSuggestionTicket {
  title: string;
  body?: string | null;
  components?: readonly string[];
  labels?: readonly string[];
  trackerKey?: string | null;
}

/**
 * `GET /api/desks/:id/repo-suggestion`: which of the Team's repositories a
 * ticket belongs to, or an honest "none fits" with a reason. Fail-soft: null
 * on a Team with one repository, a failed read, or a malformed answer.
 */
export async function fetchRepoSuggestion(
  deskId: string,
  ticket: RepoSuggestionTicket,
): Promise<RepoSuggestion | null> {
  const title = ticket.title.trim();
  if (!title) return null;
  const params = new URLSearchParams({ title: title.slice(0, 500) });
  const body = ticket.body?.trim();
  if (body) params.set("body", body.slice(0, 2000));
  if (ticket.components?.length) params.set("components", ticket.components.join(","));
  if (ticket.labels?.length) params.set("labels", ticket.labels.join(","));
  if (ticket.trackerKey?.trim()) params.set("trackerKey", ticket.trackerKey.trim());
  try {
    const res = await fetch(`${deskBase(deskId)}/repo-suggestion?${params.toString()}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    return readRepoSuggestion(await res.json().catch(() => null));
  } catch {
    return null;
  }
}

/** A `RepoSuggestion` off the wire, or null when it is not one. */
function readRepoSuggestion(raw: unknown): RepoSuggestion | null {
  if (!raw || typeof raw !== "object") return null;
  const entry = raw as { repoId?: unknown; repoFullName?: unknown; reason?: unknown };
  const reason = typeof entry.reason === "string" ? entry.reason.trim() : "";
  if (typeof entry.repoId === "string" && typeof entry.repoFullName === "string") {
    return { repoId: entry.repoId, repoFullName: entry.repoFullName, reason };
  }
  if (entry.repoId == null && entry.repoFullName == null && reason) {
    return { repoId: null, repoFullName: null, reason };
  }
  return null;
}

/**
 * The roster, the answerers, and whose tickets the Team works from.
 *
 * Every part is normalised rather than trusted, because a Team's people are
 * rendered as a list a person edits: a body missing `members` would otherwise
 * take the section down instead of showing an empty roster, and an empty
 * roster is the honest reading of "we could not find anybody". An older
 * server that sends no `ticketsAssignedTo` reads as anyone's.
 */
export function fetchTeamMembers(deskId: string): Promise<TeamMembersResponse> {
  return get<Partial<TeamMembersResponse> | null>(`${deskBase(deskId)}/members`).then((body) => ({
    members: Array.isArray(body?.members) ? body.members : [],
    answerers: body?.answerers ?? {},
    ticketsAssignedTo: readTicketOwners(body?.ticketsAssignedTo),
    trackers: Array.isArray(body?.trackers)
      ? body.trackers.filter(
          (tracker): tracker is TeamTracker => tracker === "jira" || tracker === "linear",
        )
      : [],
  }));
}

function readTicketOwners(value: unknown): TeamTicketOwners {
  if (!Array.isArray(value)) return null;
  const owners = value.filter(
    (entry): entry is string => typeof entry === "string" && entry !== "",
  );
  return owners.length > 0 ? owners : null;
}

export function fetchJobRecord(deskId: string, jobId: string): Promise<TeamJobRecord> {
  return get<{ record: TeamJobRecord }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/record`,
  ).then((body) => body.record);
}

/**
 * A line's recent runs. A Harper line also carries its engine's own runs and
 * the slots that never posted; every other line carries `runs` alone.
 */
export function fetchJobRuns(deskId: string, jobId: string): Promise<TeamJobRunsResponse> {
  return get<TeamJobRunsResponse>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/runs`,
  ).then((body) => ({
    runs: body.runs ?? [],
    ...(body.harperRuns ? { harperRuns: body.harperRuns } : {}),
    ...(body.missedSlots ? { missedSlots: body.missedSlots } : {}),
  }));
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export interface PatchJobBody {
  trigger?: unknown;
  filter?: unknown;
  note?: string | null;
  authority?: JobAuthority;
  supervisorUserId?: string | null;
  active?: boolean;
  delivery?: unknown;
  /** A `chronicler.custom` line's engine body: what it looks for, and how. */
  body?: Partial<JobBody>;
}

export function patchJob(deskId: string, jobId: string, body: PatchJobBody): Promise<Job> {
  return send<{ job: Job }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}`,
    "PATCH",
    body,
  ).then((res) => res.job);
}

export interface CreateJobBody {
  personaSlug: string;
  kind: string;
  instance: string;
  trigger?: unknown;
  filter?: unknown;
  note?: string | null;
  supervisorUserId?: string | null;
  active?: boolean;
  /**
   * A `chronicler.custom` line's engine body: its Written as, and its
   * instructions (required only on a Custom line).
   */
  body?: { category: WrittenAs; instructions?: string; approach?: HarperApproach };
}

/**
 * Add a line.
 *
 * The service ignores an `authority` on create: a fresh instance always starts
 * at the kind's `startsAt`. Setting a rung on a brand-new line is therefore a
 * create followed by a patch, which the open card does explicitly rather than
 * pretending one call did it.
 */
export function createJob(deskId: string, body: CreateJobBody): Promise<Job> {
  return send<{ job: Job }>(`${deskBase(deskId)}/jobs`, "POST", body).then((res) => res.job);
}

export function deleteJob(deskId: string, jobId: string): Promise<void> {
  return send<void>(`${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}`, "DELETE");
}

export function runJobNow(deskId: string, jobId: string): Promise<{ runId: string | null }> {
  return send<{ ok: boolean; runId: string | null }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/run`,
    "POST",
    { nonce: crypto.randomUUID() },
  ).then((body) => ({ runId: body.runId ?? null }));
}

/**
 * Retry this line's latest unsettled run now — the Retry button on a failed
 * firing. Same targeted-retry path as the run detail: stage-preserved resume,
 * no re-billing of completed stages, no double-post.
 */
export interface RetryLatestResult {
  /** The retried run's status: `completed`, or the way it stopped again. */
  status: string;
  runId: string;
  /** Why it stopped again, when it did. */
  failure: { stage: string; reason: string; code?: string } | null;
}

export function retryLatestRun(deskId: string, jobId: string): Promise<RetryLatestResult> {
  return send<{
    result: {
      status: string;
      runId: string;
      storyId: string | null;
      workItemId: string | null;
      failure?: { stage: string; reason: string; code?: string } | null;
    };
  }>(`${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/retry-latest`, "POST").then((body) => ({
    status: body.result.status,
    runId: body.result.runId,
    failure: body.result.failure ?? null,
  }));
}

/**
 * Move a repository's reviews to this Team's review line.
 *
 * The answer is the Team's whole line status, because the sentence the person
 * clicked is derived from what the act changed and the page must not have to
 * ask again to see it.
 */
export function claimRepoForLine(
  deskId: string,
  jobId: string,
  repo: string,
): Promise<JobStatus[]> {
  return send<{ statuses: JobStatus[] }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/act`,
    "POST",
    { act: "reviewer.claim_repo", repo },
  ).then((body) => body.statuses ?? []);
}

/** What the workspace's connected trackers hold, for the backlog pickers. */
export function fetchTrackerOptions(): Promise<TrackerOptionsResponse> {
  return get<TrackerOptionsResponse>("/api/desks/tracker-options");
}

/** What the trackers hold that matches a few words or a pasted link. */
export function searchTrackerBacklogs(query: string): Promise<TrackerSearchResponse> {
  return get<TrackerSearchResponse>(`/api/desks/tracker-search?q=${encodeURIComponent(query)}`);
}

/**
 * Attach a backlog (a tracker project, epic or view) to the Team. The Backlog
 * blank on a propose line writes this first when it names one the Team does
 * not have yet, so the line never names a backlog its Team cannot read.
 */
export function attachBacklog(
  deskId: string,
  attachment: { kind: string; source: string; ref: string; label?: string | null },
): Promise<unknown> {
  // The parsed 201 body: it may carry `backlogAccess` (read it with
  // `readBacklogAccess`).
  return send<unknown>(`${deskBase(deskId)}/attachments`, "POST", {
    kind: attachment.kind,
    source: attachment.source,
    ref: attachment.ref,
    label: attachment.label ?? null,
  });
}

export function pauseTeam(deskId: string): Promise<void> {
  return send<void>(`${deskBase(deskId)}/pause`, "POST");
}

export function resumeTeam(deskId: string): Promise<void> {
  return send<void>(`${deskBase(deskId)}/resume`, "POST");
}

/** The viewer takes themselves off the Team: its roster, answerers and readers. */
export function leaveTeam(deskId: string): Promise<void> {
  return send<void>(`${deskBase(deskId)}/leave`, "POST");
}

export function addTeamMember(deskId: string, userId: string): Promise<TeamMember> {
  return send<{ member: TeamMember }>(`${deskBase(deskId)}/members`, "POST", {
    userId,
  }).then((body) => body.member);
}

/** Take a person off the Team. The owner cannot be removed; nor can someone who still supervises a line. */
export function removeTeamMember(deskId: string, userId: string): Promise<void> {
  return send<void>(`${deskBase(deskId)}/members/${encodeURIComponent(userId)}`, "DELETE");
}

/**
 * Make a workspace connection one of the Team's connected tools. Additive: the
 * Team's other tools and its triggers stay as they are. A source that is not
 * connected in the workspace, or a reader who cannot change the Team, is
 * refused with a `TeamWriteError` carrying the server's sentence.
 */
export function addTeamConnectedTool(deskId: string, source: string): Promise<void> {
  return send<unknown>(`${deskBase(deskId)}/connected-tools`, "POST", { source }).then(
    () => undefined,
  );
}

/**
 * Hide a workspace connection from the Team (YAG-986): it leaves the Team's
 * reads and its own list alike. The same route shown again by
 * `addTeamConnectedTool`.
 */
export function hideTeamConnection(deskId: string, source: string): Promise<void> {
  return send<unknown>(
    `${deskBase(deskId)}/connected-tools/${encodeURIComponent(source)}`,
    "DELETE",
  ).then(() => undefined);
}

/** What the Team reads, both layers (`GET /api/desks/:id/reads`). */
export function fetchTeamReads(deskId: string): Promise<TeamReadsResponse> {
  return get<Partial<TeamReadsResponse> | null>(`${deskBase(deskId)}/reads`).then((body) => ({
    deskId: typeof body?.deskId === "string" ? body.deskId : deskId,
    canManage: body?.canManage === true,
    connections: Array.isArray(body?.connections) ? body.connections : [],
  }));
}

/** Grant a container (a Jira project, a Slack channel) to the Team: the existing grant route. */
export function grantContainerToTeam(deskId: string, containerId: string): Promise<void> {
  return send<unknown>(`${deskBase(deskId)}/container-grants`, "POST", { containerId }).then(
    () => undefined,
  );
}

/** Open a container to the whole workspace: the admin lever on Connections. */
export function openContainerToWorkspace(source: string, containerId: string): Promise<void> {
  return send<unknown>(
    `/api/connections/${encodeURIComponent(source)}/containers/${encodeURIComponent(containerId)}`,
    "PATCH",
    { accessOverride: "workspace" },
  ).then(() => undefined);
}

/** Which Teams read a connection, for the connector sheet (admin). */
export function fetchConnectionTeams(source: string): Promise<ConnectionTeamsResponse> {
  return get<Partial<ConnectionTeamsResponse> | null>(
    `/api/connections/${encodeURIComponent(source)}/teams`,
  ).then((body) => ({
    source: typeof body?.source === "string" ? body.source : source,
    personal: body?.personal === true,
    teams: Array.isArray(body?.teams) ? body.teams : [],
  }));
}

/** Rename the Team (the same charter write Team settings' Name makes). */
export function renameTeam(deskId: string, name: string): Promise<void> {
  return send<unknown>(`${deskBase(deskId)}/charter`, "PATCH", { name }).then(() => undefined);
}

/** A person in the workspace, as `GET /api/members` lists them. */
export interface WorkspacePerson {
  userId: string;
  displayName: string | null;
  email: string | null;
  avatarUrl: string | null;
}

/** Everyone in the workspace, for picking who to add to a Team. */
export function fetchWorkspacePeople(): Promise<WorkspacePerson[]> {
  return get<{ members?: unknown } | null>("/api/members").then((body) => {
    const list = Array.isArray(body?.members)
      ? (body.members as Array<Record<string, unknown>>)
      : [];
    return list
      .filter((entry) => typeof entry?.userId === "string")
      .map((entry) => ({
        userId: entry.userId as string,
        displayName: typeof entry.displayName === "string" ? entry.displayName : null,
        email: typeof entry.email === "string" ? entry.email : null,
        avatarUrl: typeof entry.avatarUrl === "string" ? entry.avatarUrl : null,
      }));
  });
}

export function saveAnswerers(deskId: string, answerers: TeamAnswerers): Promise<TeamAnswerers> {
  return send<{ answerers: TeamAnswerers }>(`${deskBase(deskId)}/answerers`, "PUT", answerers).then(
    (body) => body.answerers,
  );
}

/**
 * Whose tracker tickets this Team works from: Team member ids and
 * `"unassigned"`; an empty list is anyone's. Everyone named must already be
 * on the Team.
 */
export function saveTicketOwners(
  deskId: string,
  assignedTo: readonly string[],
): Promise<string[] | null> {
  return send<{ ticketsAssignedTo: string[] | null }>(`${deskBase(deskId)}/ticket-owners`, "PUT", {
    assignedTo,
  }).then((body) => body.ticketsAssignedTo);
}

/**
 * Put an Agent on this Team. Engaging seeds its default lines; the server
 * indexes any of the Team's repositories not indexed yet, in the background.
 */
export async function engageAgent(deskId: string, personaSlug: string): Promise<void> {
  await send<unknown>(`/api/workers/${encodeURIComponent(personaSlug)}/engagements`, "POST", {
    deskId,
  });
}

// ---------------------------------------------------------------------------
// Preview: a Harper line over the last seven days, posting nothing
// ---------------------------------------------------------------------------

/** The line's latest preview, or null when it was never previewed. */
export function fetchPreview(deskId: string, jobId: string): Promise<HarperPreview | null> {
  return get<{ preview: HarperPreview | null }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/preview`,
  ).then((body) => body.preview ?? null);
}

/** Unsaved changes a preview runs under; absent fields read as saved. */
export interface PreviewDraft {
  instructions?: string | null;
  approach?: HarperApproach;
  notRelevant?: string | null;
  category?: WrittenAs;
}

/** Start a preview (saved settings, or a draft of unsaved ones). */
export function startPreview(
  deskId: string,
  jobId: string,
  draft: PreviewDraft | null,
): Promise<HarperPreview> {
  return send<{ preview: HarperPreview }>(
    `${deskBase(deskId)}/jobs/${encodeURIComponent(jobId)}/preview`,
    "POST",
    draft ? { draft } : {},
  ).then((body) => body.preview);
}
